import { useState, useEffect } from 'react'
import { useLanguage } from '../context/LanguageContext'
import { useAuth } from '../context/AuthContext'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Label } from '../components/ui/label'
import { Badge } from '../components/ui/badge'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog'
import { Textarea } from '../components/ui/textarea'
import { 
  Users, UserPlus, Eye, Edit, PenLine, Loader2, 
  Send, Trash2, X, Check, MoreVertical
} from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import axios from 'axios'
import { toast } from 'sonner'

const API = process.env.REACT_APP_BACKEND_URL

export function CollaboratorsModal({ open, onOpenChange, module = 'finance' }) {
  const { t } = useLanguage()
  const { token } = useAuth()
  
  const [collaborators, setCollaborators] = useState([])
  const [pendingInvites, setPendingInvites] = useState([])
  const [loading, setLoading] = useState(true)
  const [showInviteForm, setShowInviteForm] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteRole, setInviteRole] = useState('viewer')
  const [inviteMessage, setInviteMessage] = useState('')
  const [sending, setSending] = useState(false)

  const fetchCollaborators = async () => {
    try {
      setLoading(true)
      const res = await axios.get(
        `${API}/api/collaborations/my-collaborators`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          params: { module }
        }
      )
      setCollaborators(res.data.collaborators_by_module?.[module] || [])
      setPendingInvites(res.data.pending_invitations || [])
    } catch (error) {
      console.error('Error fetching collaborators:', error)
      toast.error(t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (open) {
      fetchCollaborators()
    }
  }, [open, module])

  const handleSendInvitation = async () => {
    if (!inviteEmail.trim()) {
      toast.error(t('collaboration.enterEmail'))
      return
    }
    
    try {
      setSending(true)
      await axios.post(
        `${API}/api/collaborations/invite`,
        {
          email: inviteEmail.toLowerCase(),
          module: module,
          role: inviteRole,
          message: inviteMessage || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('collaboration.invitationSent'))
      setShowInviteForm(false)
      setInviteEmail('')
      setInviteRole('viewer')
      setInviteMessage('')
      fetchCollaborators()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    } finally {
      setSending(false)
    }
  }

  const handleUpdateRole = async (collabId, newRole) => {
    try {
      await axios.put(
        `${API}/api/collaborations/${collabId}`,
        { role: newRole },
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('collaboration.roleUpdated'))
      fetchCollaborators()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    }
  }

  const handleRevokeAccess = async (collabId) => {
    try {
      await axios.delete(
        `${API}/api/collaborations/${collabId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('collaboration.accessRevoked'))
      fetchCollaborators()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    }
  }

  const handleCancelInvitation = async (inviteId) => {
    try {
      await axios.delete(
        `${API}/api/collaborations/invitations/${inviteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      )
      toast.success(t('collaboration.invitationCancelled'))
      fetchCollaborators()
    } catch (error) {
      toast.error(error.response?.data?.detail || t('common.error'))
    }
  }

  const getRoleIcon = (role) => {
    switch (role) {
      case 'viewer': return <Eye className="w-4 h-4" />
      case 'capturista': return <PenLine className="w-4 h-4" />
      case 'editor': return <Edit className="w-4 h-4" />
      default: return <Eye className="w-4 h-4" />
    }
  }

  const getRoleBadge = (role) => {
    const roleColors = {
      viewer: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
      capturista: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
      editor: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
    }
    
    return (
      <Badge className={`${roleColors[role]} flex items-center gap-1`}>
        {getRoleIcon(role)}
        {t(`collaboration.roles.${role}`)}
      </Badge>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            {t('collaboration.manageCollaborators')}
          </DialogTitle>
          <DialogDescription>
            {t('collaboration.manageCollaboratorsDesc')}
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {/* Invite Button */}
            {!showInviteForm && (
              <Button
                onClick={() => setShowInviteForm(true)}
                className="w-full"
                data-testid="invite-collaborator-btn"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                {t('collaboration.inviteCollaborator')}
              </Button>
            )}

            {/* Invite Form */}
            {showInviteForm && (
              <div className="p-4 border rounded-lg space-y-3 bg-muted/50">
                <div className="flex items-center justify-between">
                  <h3 className="font-medium text-sm">{t('collaboration.newInvitation')}</h3>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-6 w-6"
                    onClick={() => setShowInviteForm(false)}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                
                <div className="space-y-2">
                  <Label>{t('collaboration.email')} *</Label>
                  <Input
                    type="email"
                    placeholder="colaborador@email.com"
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    data-testid="invite-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>{t('collaboration.role')}</Label>
                  <Select value={inviteRole} onValueChange={setInviteRole}>
                    <SelectTrigger data-testid="invite-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="viewer">
                        <div className="flex items-center gap-2">
                          <Eye className="w-4 h-4" />
                          {t('collaboration.roles.viewer')} - {t('collaboration.roleDesc.viewer')}
                        </div>
                      </SelectItem>
                      <SelectItem value="capturista">
                        <div className="flex items-center gap-2">
                          <PenLine className="w-4 h-4" />
                          {t('collaboration.roles.capturista')} - {t('collaboration.roleDesc.capturista')}
                        </div>
                      </SelectItem>
                      <SelectItem value="editor">
                        <div className="flex items-center gap-2">
                          <Edit className="w-4 h-4" />
                          {t('collaboration.roles.editor')} - {t('collaboration.roleDesc.editor')}
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>{t('collaboration.personalMessage')}</Label>
                  <Textarea
                    placeholder={t('collaboration.messagePlaceholder')}
                    value={inviteMessage}
                    onChange={(e) => setInviteMessage(e.target.value)}
                    className="h-20 resize-none"
                    data-testid="invite-message-input"
                  />
                </div>

                <Button
                  onClick={handleSendInvitation}
                  disabled={sending}
                  className="w-full"
                  data-testid="send-invitation-btn"
                >
                  {sending ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Send className="w-4 h-4 mr-2" />
                  )}
                  {t('collaboration.sendInvitation')}
                </Button>
              </div>
            )}

            {/* Pending Invitations */}
            {pendingInvites.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('collaboration.pendingInvitations')} ({pendingInvites.length})
                </h3>
                {pendingInvites.map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-amber-50/50 dark:bg-amber-900/10"
                  >
                    <div>
                      <p className="text-sm font-medium">{invite.invitee_email}</p>
                      <div className="flex items-center gap-2 mt-1">
                        {getRoleBadge(invite.role)}
                        <span className="text-xs text-muted-foreground">
                          {t('collaboration.pending')}
                        </span>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleCancelInvitation(invite.id)}
                      className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      data-testid={`cancel-invite-${invite.id}`}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            {/* Active Collaborators */}
            {collaborators.length > 0 && (
              <div className="space-y-2">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {t('collaboration.activeCollaborators')} ({collaborators.filter(c => c.is_active).length})
                </h3>
                {collaborators.filter(c => c.is_active).map((collab) => (
                  <div
                    key={collab.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div>
                      <p className="text-sm font-medium">{collab.collaborator_name}</p>
                      <p className="text-xs text-muted-foreground">{collab.collaborator_email}</p>
                      <div className="mt-1">
                        {getRoleBadge(collab.role)}
                      </div>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" data-testid={`collab-menu-${collab.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleUpdateRole(collab.id, 'viewer')}>
                          <Eye className="w-4 h-4 mr-2" />
                          {t('collaboration.changeToViewer')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdateRole(collab.id, 'capturista')}>
                          <PenLine className="w-4 h-4 mr-2" />
                          {t('collaboration.changeToCapturista')}
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleUpdateRole(collab.id, 'editor')}>
                          <Edit className="w-4 h-4 mr-2" />
                          {t('collaboration.changeToEditor')}
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleRevokeAccess(collab.id)}
                          className="text-red-600"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          {t('collaboration.revokeAccess')}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                ))}
              </div>
            )}

            {/* Empty State */}
            {collaborators.length === 0 && pendingInvites.length === 0 && !showInviteForm && (
              <div className="text-center py-6 text-muted-foreground">
                <Users className="w-10 h-10 mx-auto mb-2 opacity-50" />
                <p className="text-sm">{t('collaboration.noCollaborators')}</p>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  )
}

export default CollaboratorsModal
